import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Language, dictionary } from "../../../utils/dictionary";
import { getMetaOAuthUrl, getMetaIntegration } from "../../../services/meta/metaService";
import metaLogo from "../../../assets/images/meta-logo.png";

interface ModalMetaIntegrationProps {
  setModalOpen: (open: boolean) => void;
  handleOAuthComplete: () => void;
  loading: boolean;
  language: Language;
}

const ModalMetaIntegration = ({
  setModalOpen,
  handleOAuthComplete,
  loading,
  language,
}: ModalMetaIntegrationProps) => {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleConnect = async () => {
    setError(null);
    setConnecting(true);
    try {
      const response = await getMetaOAuthUrl();
      const popup = window.open(
        response.authorizationUrl,
        "meta-oauth",
        "width=600,height=700,scrollbars=yes"
      );

      intervalRef.current = setInterval(async () => {
        if (popup?.closed) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          try {
            const integration = await getMetaIntegration();
            if (integration?.is_active) {
              setConnecting(false);
              handleOAuthComplete();
              return;
            }
          } catch {
            // Integration not found or error
          }
          setConnecting(false);
          setError(dictionary["meta-connect-error"][language]);
        }
      }, 500);
    } catch (err: any) {
      console.error("Meta OAuth failed:", err);
      setError(err?.message || dictionary["meta-connect-error"][language]);
      setConnecting(false);
    }
  };

  const isLoading = loading || connecting;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-xl w-full max-w-md relative">
        <button
          className="absolute top-3 md:top-4 right-3 md:right-4 z-10"
          onClick={() => setModalOpen(false)}
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 flex items-center justify-center mb-4">
            <img
              src={metaLogo}
              alt="Meta"
              className="w-full h-full object-contain"
            />
          </div>

          <h2 className="text-xl md:text-2xl font-bold mb-2">
            {dictionary["meta-connect-title"][language]}
          </h2>

          <p className="text-sm text-gray-600 mb-6">
            {dictionary["meta-connect-description"][language]}
          </p>

          {error && (
            <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            className="w-full px-4 py-3 bg-[#0081FB] text-white rounded-lg hover:bg-[#006AD4] transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleConnect}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              dictionary["meta-connect-button"][language]
            )}
          </button>

          <button
            className="w-full mt-3 px-4 py-2 text-sm md:text-base bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
            onClick={() => setModalOpen(false)}
          >
            {dictionary["cancel"][language]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalMetaIntegration;
